import { Toggle } from "@/components/custom/toggle";
import { ColumnDef, Row } from "@tanstack/react-table";
import { SingleSelectCell } from "./data-table-single-select-cell";

/**
 * Single-select column for data tables using radio-button style toggle.
 *
 * Usage:
 * 1. Add `enableMultiRowSelection: false` to your table context configuration
 * 2. Include `singleSelectColumn` as the first column in your columns array
 *
 * Example:
 * ```tsx
 * import { singleSelectColumn } from "@/components/custom/data-table/data-table-single-select-column";
 *
 * export const MyTableContext = createTableContext<MyRow, MyArgs>({
 *   enableMultiRowSelection: false,
 *   columns: () => [singleSelectColumn as ColumnDef<MyRow, unknown>, ...otherColumns],
 * });
 * ```
 *
 * Header: Shows pressed state when any row is selected. Clicking clears selection.
 * Cell: Radio-style toggle. Clicking selects that row (auto-deselects others when enableMultiRowSelection is false).
 *
 * Features:
 * - Auto-deselect: If a row becomes disabled while selected, it will be automatically deselected
 * - Initial selection: Use `getIsInitiallySelected` to set a row as selected on initial render
 */
export type SingleSelectColumnOptions = {
  /**
   * Determine if a row should be disabled
   */
  getIsDisabled?: (row: Row<unknown>) => boolean;
  /**
   * Determine if a row should be initially selected (runs once on mount)
   */
  getIsInitiallySelected?: (row: Row<unknown>) => boolean;
};

export const createSingleSelectColumn = (
  options: SingleSelectColumnOptions = {},
): ColumnDef<unknown, unknown> => ({
  id: "select",
  header: ({ table }) => {
    const hasSelection =
      Object.keys(table.getState().rowSelection).length > 0 ||
      table.getAllSelected();

    return (
      <Toggle
        variant="round_dot"
        size="xs"
        pressed={hasSelection}
        onClick={(ev) => ev.stopPropagation()}
        onPressedChange={() => {
          table.setAllSelected(false);
          table.resetRowSelection();
        }}
        aria-label="Clear selection"
        className="translate-y-[2px]"
      />
    );
  },
  cell: ({ row }) => <SingleSelectCell row={row} options={options} />,
  size: 20,
  enableResizing: false,
  enableSorting: false,
  enableHiding: false,
  meta: {
    box: {
      hidden: true,
    },
  },
});

export const singleSelectColumn: ColumnDef<unknown, unknown> =
  createSingleSelectColumn();
